import { AuthGuard } from "@ssf/auth-components";
import { Loading, useTrackPageChange } from "@ssf/components";
import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router";

import { routes } from "../lib/index.js";
import { ClubGuard } from "./index.js";

const SelectClubPage = lazy(() => import("../pages/SelectClubPage.jsx"));
const ClubsPage = lazy(() => import("../pages/ClubsPage.jsx"));
const ClubsPageV2 = lazy(() => import("../pages/ClubsPageV2.jsx"));
const CreateClubPage = lazy(() => import("../pages/CreateClubPage.jsx"));
const ClubPage = lazy(() => import("../pages/ClubPage.jsx"));
const MembersPage = lazy(() => import("../pages/MembersPage.jsx"));
const MemberPage = lazy(() => import("../pages/MemberPage.jsx"));
const NewMemberPage = lazy(() => import("../pages/NewMemberPage.jsx"));
const ConstituentMeetingPage = lazy(
  () => import("../pages/ConstituentMeetingPage.jsx"),
);
const StatutesPage = lazy(() => import("../pages/StatutesPage.jsx"));
const AnnualMeetingPage = lazy(() => import("../pages/AnnualMeetingPage.jsx"));
const AnnualReportPage = lazy(() => import("../pages/AnnualReportPage.jsx"));
const DocPage = lazy(() => import("../pages/DocPage.jsx"));
const ClubDocPage = lazy(() => import("../pages/ClubDocPage.jsx"));
const SignedOutPage = lazy(() => import("../pages/SignedOutPage.jsx"));
const SignaturesPage = lazy(() => import("../pages/SignaturesPage.jsx"));
const OrdersPage = lazy(() => import("../pages/OrdersPage.jsx"));
const ClubSignaturesPage = lazy(
  () => import("../pages/ClubSignaturesPage.jsx"),
);
const SignMembershipPage = lazy(
  () => import("../pages/SignMembershipPage.jsx"),
);
const ImportMembersPage = lazy(() => import("../pages/ImportMembersPage.jsx"));
const ImportMembersPageV2 = lazy(
  () => import("../pages/ImportMembersPageV2.jsx"),
);
const TransferMembersPage = lazy(
  () => import("../pages/TransferMembersPage.jsx"),
);
const SignPage = lazy(() => import("../pages/SignPage.jsx"));
const ClubSignatureListsPage = lazy(
  () => import("../pages/ClubSignatureListsPage.jsx"),
);
const ValidateClubSignaturesPage = lazy(
  () => import("../pages/ValidateClubSignaturesPage.jsx"),
);
const AvailabilitySettingsPage = lazy(
  () => import("../pages/AvailabilitySettingsPage.jsx"),
);
const SupportPage = lazy(() => import("../pages/SupportPage.jsx"));

const guardedRoutes = [
  [routes.club, ClubPage],
  [routes.members, MembersPage],
  [routes.member, MemberPage],
  [routes.signMembership, SignMembershipPage],
  [routes.newMember, NewMemberPage],
  [routes.constituentMeeting, ConstituentMeetingPage],
  [routes.statues, StatutesPage],
  [routes.annualMeeting, AnnualMeetingPage],
  [routes.annualReport, AnnualReportPage],
  [routes.clubDocuments, ClubDocPage],
  [routes.clubSignatures, ClubSignaturesPage],
  [routes.clubSignatureLists, ClubSignatureListsPage],
  [routes.validateClubSignatures, ValidateClubSignaturesPage],
  [routes.importMembers, ImportMembersPage],
  [routes.importMembersNew, ImportMembersPageV2],
  [routes.moveMembers, TransferMembersPage],
] as const;
function generateGuardedRoutes() {
  return (
    <>
      {guardedRoutes.map(([route, Page]) => (
        <Route
          key={route}
          path={route}
          element={
            <AuthGuard autoSignin>
              {(user) => (
                <ClubGuard>
                  {(club) => (
                    <Suspense fallback={<Loading />}>
                      <Page user={user} club={club} />
                    </Suspense>
                  )}
                </ClubGuard>
              )}
            </AuthGuard>
          }
        />
      ))}
    </>
  );
}

export const Router = () => {
  useTrackPageChange();

  return (
    <Routes>
      <Route
        path={routes.selectClub}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <SelectClubPage />
            </Suspense>
          </AuthGuard>
        }
      />
      <Route
        path={routes.createClub}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <CreateClubPage />
            </Suspense>
          </AuthGuard>
        }
      />
      <Route
        path={routes.availabilitySettings}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <AvailabilitySettingsPage />
            </Suspense>
          </AuthGuard>
        }
      />
      <Route
        path={routes.support}
        element={
          <Suspense fallback={<Loading />}>
            <SupportPage />
          </Suspense>
        }
      />
      {generateGuardedRoutes()}
      <Route
        path="/dokument"
        element={
          <Suspense fallback={<Loading />}>
            <DocPage />
          </Suspense>
        }
      />
      <Route
        path={routes.document}
        element={
          <Suspense fallback={<Loading />}>
            <DocPage />
          </Suspense>
        }
      />
      <Route
        path={routes.signedOut}
        element={
          <Suspense fallback={<Loading />}>
            <SignedOutPage />
          </Suspense>
        }
      />
      <Route
        path={routes.orders}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <OrdersPage />
            </Suspense>
          </AuthGuard>
        }
      />
      <Route
        path={routes.signatures}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <SignaturesPage />
            </Suspense>
          </AuthGuard>
        }
      />
      <Route
        path={routes.sign}
        element={
          <Suspense fallback={<Loading />}>
            <SignPage />
          </Suspense>
        }
      />
      <Route
        path={routes.clubs}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <ClubsPage />
            </Suspense>
          </AuthGuard>
        }
      />
      <Route
        path={routes.districts}
        element={
          <AuthGuard autoSignin>
            <Suspense fallback={<Loading />}>
              <ClubsPageV2 />
            </Suspense>
          </AuthGuard>
        }
      />
    </Routes>
  );
};
